import React, { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { isMultiCompany } from './utils/multiCompany';
import LoginPage from './components/auth/LoginPage';
import Layout from './components/layout/Layout';

// Solo /webphone registra el softphone: sip.js baja con esa ruta, no con el login.
const SoftphoneProvider = lazy(() => import('./context/SoftphoneContext').then((m) => ({ default: m.SoftphoneProvider })));
// Solo una ventana del Marcador por navegador: la duplicada no monta el softphone (no registra).
const WebphoneGate = lazy(() => import('./components/dialer/WebphoneGate'));
// El Dashboard arrastra recharts: el login no tiene por qué descargarlo.
const DashboardPage = lazy(() => import('./components/dashboard/DashboardPage'));

const SignupPage = lazy(() => import('./components/signup/SignupPage'));
const ExtensionsPage = lazy(() => import('./components/extensions/ExtensionsPage'));
const TrunksPage = lazy(() => import('./components/trunks/TrunksPage'));
const QueuesPage = lazy(() => import('./components/queues/QueuesPage'));
const IVRPage = lazy(() => import('./components/ivr/IVRPage'));
const RingGroupsPage = lazy(() => import('./components/ringgroups/RingGroupsPage'));
const VoicemailPage = lazy(() => import('./components/voicemail/VoicemailPage'));
const CDRPage = lazy(() => import('./components/cdr/CDRPage'));
const EventLogPage = lazy(() => import('./components/events/EventLogPage'));
const RecordingsPage = lazy(() => import('./components/recordings/RecordingsPage'));
const DialplanPage = lazy(() => import('./components/dialplan/DialplanPage'));
const ConferencesPage = lazy(() => import('./components/conferences/ConferencesPage'));
const PhonebookPage = lazy(() => import('./components/phonebook/PhonebookPage'));
const UsersPage = lazy(() => import('./components/users/UsersPage'));
const SettingsPage = lazy(() => import('./components/settings/SettingsPage'));
const SystemPage = lazy(() => import('./components/system/SystemPage'));
const MonitorPage = lazy(() => import('./components/monitor/MonitorPage'));
const WebphonePage = lazy(() => import('./components/dialer/WebphonePage'));
const IntegrationsPage = lazy(() => import('./components/integrations/IntegrationsPage'));
const AppsPage = lazy(() => import('./components/apps/AppsPage'));
const MessagingPage = lazy(() => import('./components/messaging/MessagingPage'));
const CompaniesPage = lazy(() => import('./components/companies/CompaniesPage'));
const PlansPage = lazy(() => import('./components/plans/PlansPage'));
const SupportTicketsPage = lazy(() => import('./components/support/SupportTicketsPage'));
const AIAgentPage = lazy(() => import('./components/ai-agent/AIAgentPage'));


// Mientras baja el trozo de una página (solo la primera vez que se entra a ella).
function PageFallback() {
  return <div role="status" className="p-8 text-sm text-gray-500">Cargando…</div>;
}

// multiOnly: administración del propietario; solo existe en instalaciones Multi Company.
function ProtectedRoute({ children, roles = [], multiOnly = false }) {
  const { user, token } = useAuthStore();
  if (!token || !user) return <Navigate to="/login" replace />;
  if (multiOnly && !isMultiCompany()) return <Navigate to="/" replace />;
  if (roles.length > 0 && !roles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }
  return children;
}

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/webphone" element={
          <ProtectedRoute>
            <WebphoneGate>
              <SoftphoneProvider>
                <WebphonePage />
              </SoftphoneProvider>
            </WebphoneGate>
          </ProtectedRoute>
        } />
        <Route path="/" element={
          <ProtectedRoute>
            <Layout />
          </ProtectedRoute>
        }>
          <Route index element={<DashboardPage />} />
          <Route path="extensions" element={<ExtensionsPage />} />
          <Route path="trunks" element={
            <ProtectedRoute roles={['admin','superadmin']}>
              <TrunksPage />
            </ProtectedRoute>
          } />
          <Route path="queues" element={<QueuesPage />} />
          <Route path="ivr" element={
            <ProtectedRoute roles={['admin','superadmin']}>
              <IVRPage />
            </ProtectedRoute>
          } />
          <Route path="ring-groups" element={
            <ProtectedRoute roles={['admin','superadmin']}>
              <RingGroupsPage />
            </ProtectedRoute>
          } />
          <Route path="voicemail" element={<VoicemailPage />} />
          <Route path="cdr" element={<CDRPage />} />
          <Route path="events" element={<EventLogPage />} />
          <Route path="recordings" element={<RecordingsPage />} />
          <Route path="dialplan" element={
            <ProtectedRoute roles={['admin','superadmin']}>
              <DialplanPage />
            </ProtectedRoute>
          } />
          <Route path="conferences" element={<ConferencesPage />} />
          <Route path="phonebook" element={<PhonebookPage />} />
          <Route path="companies" element={
            <ProtectedRoute roles={['superadmin']} multiOnly>
              <CompaniesPage />
            </ProtectedRoute>
          } />
          <Route path="plans" element={
            <ProtectedRoute roles={['superadmin']} multiOnly>
              <PlansPage />
            </ProtectedRoute>
          } />
          <Route path="users" element={
            <ProtectedRoute roles={['admin','superadmin']}>
              <UsersPage />
            </ProtectedRoute>
          } />
          <Route path="settings" element={
            <ProtectedRoute roles={['admin','superadmin']}>
              <SettingsPage />
            </ProtectedRoute>
          } />
          <Route path="system" element={
            <ProtectedRoute roles={['admin','superadmin']}>
              <SystemPage />
            </ProtectedRoute>
          } />
          <Route path="integrations" element={
            <ProtectedRoute roles={['admin','superadmin']}>
              <IntegrationsPage />
            </ProtectedRoute>
          } />
          <Route path="apps" element={<AppsPage />} />
          <Route path="messaging" element={
            <ProtectedRoute roles={['admin','superadmin','supervisor','agent']}>
              <MessagingPage />
            </ProtectedRoute>
          } />
          <Route path="monitor" element={
            <ProtectedRoute roles={['admin','superadmin']}>
              <MonitorPage />
            </ProtectedRoute>
          } />
          <Route path="support-tickets" element={
            <ProtectedRoute multiOnly>
              <SupportTicketsPage />
            </ProtectedRoute>
          } />
          <Route path="ai-agent" element={
            <ProtectedRoute roles={['superadmin']} multiOnly>
              <AIAgentPage />
            </ProtectedRoute>
          } />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
